import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import React from "react";
import { css } from "@emotion/react";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { FONTS, fontSize } from "../../styles/fonts";
import { CSS_MQ } from "../../styles/breakpoints";
import cn from "classnames";
import { nonEmpty } from "../../utils/fp/small-fp-utils";
import { Visibility } from "../../hooks/use-visibility/get-visibility";
import { LeftArrowSvg, RightArrowSvg } from "../../assets";
import { toId } from "./to-id";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../components/T";

type LabelSection = {
  label: TranslatedValue;
  id: string;
  mobileHidden: boolean;
  visibility: Visibility;
};

type Props = {
  sections: Array<
    | {
        label: TranslatedValue;
        anchor?: string;
        node: React.ReactNode;
        hidden?: boolean;
      }
    | {
        label: TranslatedValue;
        anchor: string;
        hidden?: boolean;
        mobileHidden: boolean;
      }
  >;
  Button: React.ReactNode;
  visibility: Array<Visibility>;
};

export const PdpNavigationBar: ReactTFC<Props> = props => {
  const labelSections = props.sections.map((section, idx) => {
    const visibility = props.visibility[idx]!;

    return "node" in section
      ? {
          label: section.label,
          id: toId(section.anchor || section.label.value),
          mobileHidden: false,
          hidden: !!section.hidden,
          visibility,
        }
      : {
          label: section.label,
          id: toId(section.anchor),
          mobileHidden: section.mobileHidden,
          hidden: !!section.hidden,
          visibility,
        };
  });

  function getElements() {
    const visibleIdx = labelSections.findIndex(el => el.visibility.isVisible && el.visibility.canTopBeSeen);

    if (visibleIdx === -1) {
      const visibleIdx = labelSections.findIndex(el => el.visibility.isVisible);

      if (visibleIdx >= 0) {
        return {
          left: [labelSections[visibleIdx - 1]].filter(nonEmpty),
          middleEl: labelSections[visibleIdx],
          right: [],
        };
      }

      if (
        labelSections.every(
          s => !s.visibility.isVisible && (s.visibility.tag === "fully-above" || s.visibility.tag === "not-visible"),
        )
      ) {
        return {
          left: [],
          middleEl: undefined,
          right: [],
        };
      }

      if (labelSections.length > 1) {
        return {
          left: [labelSections[0]].filter(nonEmpty),
          middleEl: labelSections[1],
          right: [labelSections[2]].filter(nonEmpty),
        };
      } else {
        return {
          left: [],
          middleEl: undefined,
          right: [],
        };
      }
    }

    let left: Array<LabelSection> = [];
    let right: Array<LabelSection> = [];
    labelSections.forEach((el, idx) => {
      if (idx < visibleIdx) {
        left.push(el);
      } else if (idx > visibleIdx) {
        right.push(el);
      }
    });

    left = [left[left.length - 1]].filter(nonEmpty);
    right = [right[0]].filter(nonEmpty);

    const middleEl: LabelSection | undefined = labelSections[visibleIdx];

    return { left, right, middleEl };
  }

  const { left, middleEl, right } = getElements();

  return (
    <nav className={cn({ hidden: left.length === 0 && right.length === 0 && !middleEl })} css={wrapperStyles}>
      <a href={`#${left[left.length - 1]?.id}`} css={arrowStyles} className={cn("left", { hidden: left.length === 0 })}>
        <LeftArrowSvg />
      </a>

      <a href={`#${right[0]?.id}`} css={arrowStyles} className={cn("right", { hidden: right.length === 0 })}>
        <RightArrowSvg />
      </a>

      <div
        css={css`
          ${CSS_MQ.atMostMd} {
            align-items: center;
            width: 100%;
            height: 100%;
            grid-gap: 8px;
            overflow-x: auto;

            position: relative;
            --w: 240px;
            --x: calc((100% - var(--w)) / 2);
          }

          ${CSS_MQ.atMostXs} {
            --w: 180px;
          }
          ${CSS_MQ.atMostSm} {
            --w: 200px;
          }

          ${CSS_MQ.atLeastLg} {
            white-space: nowrap;
            overflow: hidden;
            display: block;
            text-overflow: ellipsis;
          }
        `}
      >
        <div
          css={css`
            display: none;
            ${CSS_MQ.atLeastLg} {
              display: flex;
              gap: var(--g);
              align-items: center;
              margin-right: var(--g);
            }
          `}
        >
          {labelSections.map(({ label, id, mobileHidden, hidden }, idx) => {
            return (
              <a
                key={id + label.key + idx}
                href={`#${id}`}
                className={cn({
                  "currently-visible": false,
                  "hide-sm": mobileHidden,
                  hide: hidden,
                })}
                css={css1}
              >
                <T>{label}</T>
              </a>
            );
          })}
        </div>

        <div
          css={css`
            ${CSS_MQ.atLeastLg} {
              display: none;
            }
          `}
        >
          <div
            css={[
              css2,
              css`
                ${CSS_MQ.atMostMd} {
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: var(--x);
                  height: 100%;
                }

                display: flex;
                justify-content: center;
                align-items: center;
              `,
            ]}
          >
            {left.map(({ label, id, mobileHidden }) => {
              return (
                <a
                  key={id + label.key}
                  href={`#${id}`}
                  className={cn({
                    "currently-visible": false,
                    "hide-sm": mobileHidden,
                  })}
                  css={[css1]}
                >
                  <T>{label}</T>
                </a>
              );
            })}
          </div>

          <div
            css={css`
              ${CSS_MQ.atMostMd} {
                position: absolute;
                left: var(--x);
                --m: 12px;
                top: 0;
                width: calc(var(--w) - 2 * var(--m));
                height: 100%;

                background-color: ${CSS_COLORS.Grey0};
                padding: 0 8px;
                margin-left: var(--m);
                margin-right: var(--m);
              }

              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            {middleEl && (
              <span
                key={middleEl.id}
                // href={`#${middleEl.id}`}
                className={cn({
                  "currently-visible": true,
                  "hide-sm": middleEl.mobileHidden,
                })}
                css={[
                  css1,
                  css`
                    font-weight: 500;
                  `,
                ]}
              >
                <T>{middleEl.label}</T>
              </span>
            )}
          </div>

          <div
            css={[
              css2,
              css`
                ${CSS_MQ.atMostMd} {
                  position: absolute;
                  left: calc(var(--x) + var(--w));
                  top: 0;
                  width: var(--x);
                  height: 100%;
                }

                display: flex;
                justify-content: center;
                align-items: center;
              `,
            ]}
          >
            {right.map(({ label, id, mobileHidden, visibility }, idx) => {
              return (
                <a
                  key={id + label.key}
                  href={`#${id}`}
                  className={cn({
                    "currently-visible": visibility.isVisible,
                    "hide-sm": mobileHidden,
                  })}
                  css={css1}
                >
                  <T>{label}</T>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;

          ${CSS_MQ.atMostMd} {
            display: none;
          }
        `}
      >
        {props.Button}
      </div>
    </nav>
  );
};

const wrapperStyles = css`
  --pdp-nav-bar-height: 48px;
  ${CSS_MQ.atLeastLg} {
    --pdp-nav-bar-height: 64px;
  }

  height: var(--pdp-nav-bar-height);

  border-top: 1px solid;
  border-bottom: 1px solid;
  border-color: ${CSS_COLORS.Grey5};

  padding: 0;
  ${CSS_MQ.atLeastMidScreen} {
    padding: 0 var(--g);
  }

  display: flex;
  justify-content: space-between;
  align-items: center;

  ${CSS_MQ.atMostMd} {
    &.hidden {
      // so that it doesn't trigger a re-layout
      // possibly changing visibility state (in a loop)
      visibility: hidden;
    }
  }
`;

const css1 = [
  FONTS.SwarovskiFutura300,
  fontSize.x2s,
  css`
    text-decoration: none;
    color: ${CSS_COLORS.GreenDark};

    white-space: nowrap;
    overflow: hidden;
    display: block;
    text-overflow: ellipsis;

    margin-top: 3px; //center vertically

    ${CSS_MQ.atMostMd} {
      &.hide-sm {
        display: none;
      }

      &:not(.currently-visible) {
        //opacity: 0.5;
      }
    }

    &.hide {
      display: none;
    }
  `,
];

const css2 = css`
  overflow-x: auto;
  display: flex;
  flex-direction: row;
`;

const arrowStyles = css`
  &.hidden {
    display: none;
  }

  ${CSS_MQ.atLeastLg} {
    display: none;
  }

  display: flex;
  align-items: center;

  --a: 5px;
  height: calc(var(--pdp-nav-bar-height) - 2 * var(--a));
  width: calc(1.5 * var(--pdp-nav-bar-height));

  position: absolute;

  &.left {
    left: 15px;
    justify-content: flex-start;
    background: linear-gradient(90deg, white, transparent);
  }

  &.right {
    right: 15px;
    justify-content: flex-end;
    background: linear-gradient(90deg, transparent, white);
  }

  top: var(--a);

  z-index: 1;
  color: ${CSS_COLORS.Grey3};

  svg {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }
`;
